<template>
  <div class="cpq-price-rules-editor">
    <div class="price-rules-header">
      <h3>Price rules</h3>
      <Btn variant="secondary" icon="plus" @click="addRule">Add price rule</Btn>
    </div>
    <div v-for="(rule, idx) in localRules" :key="rule.id" class="price-rule-card">
      <div class="price-rule-card__header">
        <div class="price-rule-card__label">
          <input
            v-model="rule.label"
            placeholder="Label (for quote)"
            class="price-rule-label-input"
          />
          <label class="show-in-quote">
            <input type="checkbox" v-model="rule.showInQuote" />
            Show in quote
          </label>
        </div>
        <Btn icon="trash" variant="tertiary" @click="removeRule(idx)" title="Verwijder regel" />
      </div>
      <div class="price-rule-card__body">
        <CPQConditionGroup :group="rule.rootGroup" :attributes="attributes" :canRemove="false" />
        <div class="rule-panel">
          <div class="rule-panel__title">Do this</div>
          <div class="rule-list-vertical">
            <div v-for="(action, aidx) in rule.actions" :key="aidx" class="rule-action-row">
              <select v-model="action.type" class="rule-select">
                <option value="add">Prijs verhogen</option>
                <option value="subtract">Prijs verlagen</option>
                <option value="discount">Korting (%)</option>
                <option value="override">Prijs overschrijven</option>
                <option value="formula">Formule</option>
              </select>
              <input
                v-if="action.type !== 'formula'"
                v-model.number="action.value"
                placeholder="Waarde"
                type="number"
                class="rule-value-input"
              />
              <FormulaEditor
                v-else
                v-model="action.expression"
                :label="'Formule'"
                :available-fields="attributeFields"
              />
              <Btn
                v-if="rule.actions.length > 1"
                icon="minus"
                variant="tertiary"
                @click="removeAction(idx, aidx)"
              />
            </div>
            <Btn icon="plus-circle" variant="tertiary" @click="addAction(idx)">Action</Btn>
          </div>
        </div>
      </div>
    </div>
    <div class="price-breakdown-card">
      <h4>Offerte breakdown</h4>
      <ul>
        <li v-for="(item, i) in breakdown" :key="i">{{ item }}</li>
      </ul>
      <div class="price-breakdown-total">
        <strong>Totaal: €{{ totalPrice }}</strong>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch, nextTick } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import CPQConditionGroup from './CPQConditionGroup.vue';
  import FormulaEditor from '@/components/inputs/FormulaEditor.vue';

  function makeEmptyGroup() {
    return {
      logic: 'AND',
      rules: [{ attribute: null, operator: '==', value: '' }],
      subGroups: [],
    };
  }

  // Utility: veilige formule-evaluatie (simpele parser)
  function evaluateFormula(expression, values) {
    if (!expression) return 0;
    // Vervang {veld} door waarde
    let expr = expression.replace(/\{([\w-]+)\}/g, (_, key) => {
      const v = values[key];
      return typeof v === 'number' ? v : parseFloat(v) || 0;
    });
    // Alleen veilige karakters
    if (!/^[-+*/(). 0-9<>=!&|IFTHENELSEhrwmatricpnlsgd]+$/i.test(expr.replace(/\d+/g, '')))
      return 0;
    try {
      // eslint-disable-next-line no-eval
      return Function(`'use strict'; return (${expr})`)();
    } catch {
      return 0;
    }
  }

  export default {
    name: 'CPQPriceRulesEditor',
    components: { Btn, CPQConditionGroup, FormulaEditor },
    props: {
      modelValue: { type: Array, required: true }, // v-model: priceRules
      attributes: { type: Array, required: true },
      basePrice: { type: Number, default: 1000 },
      previewValues: { type: Object, default: () => ({}) },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
      // Local copy for editing
      const localRules = ref(JSON.parse(JSON.stringify(props.modelValue)));
      const isUpdatingFromParent = ref(false);

      // Sync with parent
      watch(
        () => props.modelValue,
        (val) => {
          isUpdatingFromParent.value = true;
          localRules.value = JSON.parse(JSON.stringify(val));
          // Reset flag on next tick to avoid blocking legitimate updates
          nextTick(() => {
            isUpdatingFromParent.value = false;
          });
        }
      );

      watch(
        localRules,
        (val) => {
          // Prevent emitting during parent updates to avoid circular updates
          if (isUpdatingFromParent.value) {
            return;
          }
          emit('update:modelValue', JSON.parse(JSON.stringify(val)));
        },
        { deep: true }
      );
      function addRule() {
        localRules.value.push({
          id: 'r' + (localRules.value.length + 1),
          label: '',
          showInQuote: true,
          rootGroup: makeEmptyGroup(),
          actions: [{ type: 'add', value: 0 }],
        });
      }
      function removeRule(idx) {
        localRules.value.splice(idx, 1);
      }
      function addAction(ruleIdx) {
        localRules.value[ruleIdx].actions.push({ type: 'add', value: 0 });
      }
      function removeAction(ruleIdx, actionIdx) {
        localRules.value[ruleIdx].actions.splice(actionIdx, 1);
      }
      function evaluateConditionGroup(group, values) {
        const ruleResults = group.rules.map((rule) => {
          if (!rule.attribute) return false;
          const val = values[rule.attribute];
          switch (rule.operator) {
            case '==':
              return val == rule.value;
            case '!=':
              return val != rule.value;
            case '>':
              return Number(val) > Number(rule.value);
            case '>=':
              return Number(val) >= Number(rule.value);
            case '<':
              return Number(val) < Number(rule.value);
            case '<=':
              return Number(val) <= Number(rule.value);
            default:
              return false;
          }
        });
        const subGroupResults = (group.subGroups || []).map((sub) =>
          evaluateConditionGroup(sub, values)
        );
        const all = [...ruleResults, ...subGroupResults];
        return group.logic === 'AND' ? all.every(Boolean) : all.some(Boolean);
      }
      // Attributen als velden voor FormulaEditor
      const attributeFields = computed(() =>
        props.attributes.map((a) => ({ id: a.key, label: a.label, type: a.type }))
      );
      const breakdown = computed(() => {
        let price = props.basePrice;
        const lines = [`Basisprijs: €${price}`];
        localRules.value.forEach((rule) => {
          const match = evaluateConditionGroup(rule.rootGroup, props.previewValues);
          if (match) {
            rule.actions.forEach((action) => {
              if (action.type === 'add') {
                price += Number(action.value);
                if (rule.showInQuote)
                  lines.push(`${rule.label || 'Prijsverhoging'}: +€${action.value}`);
              } else if (action.type === 'subtract') {
                price -= Number(action.value);
                if (rule.showInQuote)
                  lines.push(`${rule.label || 'Prijsverlaging'}: -€${action.value}`);
              } else if (action.type === 'discount') {
                const korting = price * (Number(action.value) / 100);
                price -= korting;
                if (rule.showInQuote)
                  lines.push(`${rule.label || 'Korting'}: -€${korting.toFixed(2)}`);
              } else if (action.type === 'override') {
                price = Number(action.value);
                if (rule.showInQuote)
                  lines.push(`${rule.label || 'Prijs overschreven'}: €${action.value}`);
              } else if (action.type === 'formula') {
                const result = evaluateFormula(action.expression, props.previewValues);
                price += Number(result);
                if (rule.showInQuote) lines.push(`${rule.label || 'Formule'}: +€${result}`);
              }
            });
          }
        });
        return lines;
      });
      const totalPrice = computed(() => {
        let price = props.basePrice;
        localRules.value.forEach((rule) => {
          const match = evaluateConditionGroup(rule.rootGroup, props.previewValues);
          if (match) {
            rule.actions.forEach((action) => {
              if (action.type === 'add') price += Number(action.value);
              else if (action.type === 'subtract') price -= Number(action.value);
              else if (action.type === 'discount') price -= price * (Number(action.value) / 100);
              else if (action.type === 'override') price = Number(action.value);
              else if (action.type === 'formula')
                price += Number(evaluateFormula(action.expression, props.previewValues));
            });
          }
        });
        return price.toFixed(2);
      });
      return {
        localRules,
        addRule,
        removeRule,
        addAction,
        removeAction,
        breakdown,
        totalPrice,
        attributeFields,
      };
    },
  };
</script>

<style scoped lang="scss">
  // @import '@/assets/css/base/variables.scss';
  .cpq-price-rules-editor {
    // background: var(--brand-50);
    // border-radius: 12px;
    // padding: 2rem;
    // margin-bottom: 2rem;
    // box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  }
  .price-rules-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
  }
  .price-rule-card {
    background: #fff;
    border: 1px solid var(--slate-200);
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    margin-bottom: 1.5rem;
    padding: 1.5rem 1.5rem 1rem 1.5rem;
  }
  .price-rule-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
  }
  .price-rule-card__label {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  .price-rule-label-input {
    font-size: 1.1rem;
    font-weight: 600;
    border: 1px solid var(--slate-200);
    border-radius: 6px;
    padding: 0.25rem 0.75rem;
    background: var(--brand-50);
    min-width: 200px;
  }
  .show-in-quote {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.95rem;
    color: var(--slate-700);
  }
  .price-rule-card__body {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  .rule-panel {
    background-color: var(--slate-50);
    border-radius: 0.5rem;
    border: 1px solid var(--slate-200);
    padding: 0.5rem;
    &__title {
      display: flex;
      align-items: center;
      // justify-content: space-between;
      width: 100%;
      color: var(--slate-500);
      margin-bottom: 1rem;
    }
  }

  .rule-list {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .rule-select,
  .rule-value-input {
    border: 1px solid var(--slate-200);
    border-radius: 6px;
    padding: 0.25rem 0.75rem;
    background: #fff;
    font-size: 1rem;
    min-width: 110px;
  }
  .rule-value-input {
    min-width: 90px;
  }
  .rule-logic-pill {
    background: var(--brand-100);
    color: var(--brand-700);
    border-radius: 999px;
    padding: 0.15rem 0.75rem;
    font-size: 0.95rem;
    font-weight: 500;
    margin: 0 0.25rem;
  }
  .price-breakdown-card {
    background: #fff;
    border: 1px solid var(--slate-200);
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    margin-top: 2rem;
    padding: 1.5rem;
  }
  .price-breakdown-total {
    font-size: 1.2rem;
    font-weight: 700;
    margin-top: 1rem;
    color: var(--brand-600);
  }
  .rule-list-vertical {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .rule-condition-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
  }
  .rule-condition-where {
    min-width: 60px;
    color: var(--slate-700);
    font-weight: 500;
  }
  .rule-logic-pill-select {
    margin-left: 0.5rem;
  }
  .rule-remove-btn {
    margin-left: 0.25rem;
  }
  .rule-add-btn {
    margin-top: 0.5rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    min-width: 120px;
  }
  .sf-input.sf-input--select {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
  }
  .sf-input select,
  .sf-input input {
    border: 1px solid var(--slate-200);
    border-radius: 6px;
    padding: 0.25rem 0.75rem;
    background: #fff;
    font-size: 1rem;
    min-width: 110px;
  }
  .condition-group-rule__input {
    min-width: 120px;
  }
  .logic-selector {
    border: 1px solid var(--slate-200);
    border-radius: 999px;
    padding: 0.15rem 0.75rem;
    background: #fff;
    font-size: 0.95rem;
    font-weight: 500;
    margin-left: 0.5rem;
  }
</style>
